import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, Box, Button, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';

import {
  useDryRunReportingLeLensDataset,
  useExtendCheckReportingLeLensDataset,
} from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type { LensCard, LensDataset, LensDatasetColumn, LensDatasetOverride } from '~/types/leLens';

import { type GuardOutcome, guardOutcome, passSummaryLine } from './guardCopy';

/**
 * F8's escape hatch: the dataset's SQL, read-only with a copy button until
 * "Edit SQL" is pressed, then Save runs "a dry run and the same checks as
 * Add column" (02_RIGHT_PANEL_SPEC.md's own wording). That is the identical
 * dry-run-then-extend-check guard AddColumn.tsx runs, reused here rather
 * than a second copy of the pass/refuse rendering.
 */

type Stage =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | {
      kind: 'result';
      outcome: GuardOutcome;
      sql: string;
      columns: Array<LensDatasetColumn>;
      dryRun: { at: string; bytesProcessed: number; referencedTables: Array<string> };
    };

export type DatasetSqlProps = {
  lensId: string;
  dataset: LensDataset;
  /** Cards that read this dataset, for the guard's "N cards re-run"/"N cards would change". */
  cards: ReadonlyArray<LensCard>;
  /** Same meaning as AddColumn's: the guard 403s anyone but the Lens owner server-side. */
  canEdit: boolean;
  isOwner: boolean;
  ownerEmail: string;
  onApply: (nextDataset: LensDataset) => Promise<void>;
  /** F4's refusal offers the same escape: hand the candidate SQL to the New dataset composer
   *  instead of applying a grain-changing rewrite. */
  onNewDatasetInstead: (candidateSql: string) => void;
};

export default function DatasetSql({
  canEdit,
  cards,
  dataset,
  isOwner,
  lensId,
  onApply,
  onNewDatasetInstead,
  ownerEmail,
}: DatasetSqlProps) {
  const [editing, setEditing] = useState(false);
  const [sql, setSql] = useState(dataset.sql);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [error, setError] = useState<string | undefined>(undefined);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const dryRun = useDryRunReportingLeLensDataset();
  const extendCheck = useExtendCheckReportingLeLensDataset();

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(dataset.sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied by the browser. The SQL is still on screen to select by hand.
    }
  };

  const startEditing = () => {
    setSql(dataset.sql);
    setEditing(true);
    setStage({ kind: 'idle' });
    setError(undefined);
    setApplyError(undefined);
  };

  const cancelEditing = () => {
    setEditing(false);
    setStage({ kind: 'idle' });
    setError(undefined);
    setApplyError(undefined);
  };

  const save = async () => {
    setError(undefined);
    setStage({ kind: 'checking' });
    let dryRunResult: { columns: Array<LensDatasetColumn>; bytesProcessed: number; referencedTables: Array<string> };
    try {
      dryRunResult = await dryRun.mutateAsync(sql);
    } catch (dryRunError) {
      setStage({ kind: 'idle' });
      setError(dryRunError instanceof Error ? dryRunError.message : String(dryRunError));
      return;
    }
    try {
      const result = await extendCheck.mutateAsync({ datasetId: dataset.id, lensId, sql });
      setStage({
        columns: dryRunResult.columns,
        dryRun: {
          at: new Date().toISOString(),
          bytesProcessed: dryRunResult.bytesProcessed,
          referencedTables: dryRunResult.referencedTables,
        },
        kind: 'result',
        outcome: guardOutcome(result, dryRunResult.columns, dataset, cards),
        sql,
      });
    } catch (extendError) {
      setStage({ kind: 'idle' });
      setError(extendError instanceof Error ? extendError.message : String(extendError));
    }
  };

  const apply = async (override?: LensDatasetOverride) => {
    if (stage.kind !== 'result') {
      return;
    }
    setApplying(true);
    setApplyError(undefined);
    try {
      await onApply({
        ...dataset,
        columns: stage.columns,
        dryRun: stage.dryRun,
        sql: stage.sql,
        ...(override ? { override } : {}),
      });
      setEditing(false);
      setStage({ kind: 'idle' });
    } catch (saveError) {
      // Same reasoning as AddColumn.tsx: the backend's own schema validation can still refuse
      // this save (a card names a column the rewrite no longer returns), even after Apply
      // anyway. Rendered inline rather than left to escape as an unhandled rejection.
      setApplyError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setApplying(false);
    }
  };

  if (!canEdit) {
    return (
      <Alert severity="info" variant="outlined">
        Only this Lens&rsquo;s owner can edit this dataset&rsquo;s SQL.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.25}>
      {!editing ? (
        <>
          <Box
            component="pre"
            sx={{
              bgcolor: 'action.hover',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: 12,
              m: 0,
              overflowX: 'auto',
              p: 1.25,
            }}
          >
            {dataset.sql}
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton aria-label="Copy SQL" onClick={copy} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button onClick={startEditing} size="small">
              Edit SQL
            </Button>
          </Stack>
        </>
      ) : (
        <>
          <TextField
            disabled={stage.kind === 'checking'}
            fullWidth
            minRows={4}
            multiline
            onChange={(event) => setSql(event.target.value)}
            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
            value={sql}
          />
          <Typography color="text.secondary" variant="caption">
            Saving runs a dry run and the same checks as Add column.
          </Typography>

          {stage.kind === 'checking' && <LinearProgress />}
          {error && <Alert severity="warning">{error}</Alert>}

          {stage.kind === 'result' && stage.outcome.kind === 'pass' && (
            <Alert
              action={
                <Button disabled={applying} onClick={() => apply()} size="small" variant="contained">
                  Apply
                </Button>
              }
              severity="success"
            >
              {passSummaryLine(stage.outcome)} {stage.outcome.bytesLine}.
            </Alert>
          )}

          {stage.kind === 'result' && stage.outcome.kind === 'refuse' && (
            <Alert severity="warning">
              <Typography sx={{ fontWeight: 500 }} variant="body2">
                {stage.outcome.reasonLine} {stage.outcome.changedCards.length} card
                {stage.outcome.changedCards.length === 1 ? '' : 's'} would change:
              </Typography>
              <Box component="table" sx={{ borderCollapse: 'collapse', mt: 0.75, width: '100%' }}>
                <tbody>
                  {stage.outcome.changedCards.map((changed) => (
                    <tr key={changed.cardId}>
                      <Box component="td" sx={{ fontSize: 13, py: 0.25 }}>
                        {changed.title}
                      </Box>
                      <Box
                        component="td"
                        sx={{ fontFamily: 'monospace', fontSize: 12.5, py: 0.25, textAlign: 'right' }}
                      >
                        {changed.display}
                      </Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button onClick={() => onNewDatasetInstead(sql)} size="small" variant="contained">
                  Create a new dataset for this instead
                </Button>
                {isOwner && stage.outcome.canApplyAnyway && (
                  <Button
                    disabled={applying}
                    onClick={() => apply({ at: new Date().toISOString(), overriddenBy: ownerEmail })}
                    size="small"
                  >
                    Apply anyway
                  </Button>
                )}
              </Stack>
            </Alert>
          )}

          {applyError && <Alert severity="error">{applyError}</Alert>}

          <Stack direction="row" spacing={1}>
            <Button disabled={applying} onClick={cancelEditing}>
              Cancel
            </Button>
            <Button
              disabled={
                applying || stage.kind === 'checking' || !sql.trim() || (stage.kind === 'result' && stage.sql === sql)
              }
              onClick={save}
              variant="contained"
            >
              Save
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
